import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Accordion from '~/components/Accordion'
import AccordionGroup from '~/components/AccordionGroup'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const AccordionGroupPage: Component = () => {
  const [openValue, setOpenValue] = createSignal<string | string[] | undefined>(undefined)

  return (
    <>
      <Title>AccordionGroup - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">AccordionGroup</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Gestión inteligente de múltiples secciones colapsables. Controla la exclusividad, estilos compartidos y divisores automáticos entre componentes Accordion.
          </p>
        </div>

        <div class="space-y-12">
          {/* Exclusividad vs Simultaneidad */}
          <Card>
            <Card.Header
              title="Modos de Interacción"
              subtitle="Controla si solo un elemento puede estar abierto o si el usuario puede expandir múltiples secciones."
              icon={<Icon icon="material-symbols-light:unfold-more" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="p-6 bg-surface-muted rounded-xl border border-border space-y-4">
                  <Badge color="info" variant="soft">
                    Exclusivo (Single)
                  </Badge>
                  <p class="text-xs text-foreground-subtle">Comportamiento por defecto. Al abrir uno, se cierra el anterior.</p>
                  <AccordionGroup class="bg-surface rounded-lg shadow-sm">
                    <Accordion title="Panel A" value="a">
                      Contenido A
                    </Accordion>
                    <Accordion title="Panel B" value="b">
                      Contenido B
                    </Accordion>
                  </AccordionGroup>
                </div>

                <div class="p-6 bg-surface-muted rounded-xl border border-border space-y-4">
                  <Badge color="success" variant="soft">
                    Múltiple (Multiple)
                  </Badge>
                  <p class="text-xs text-foreground-subtle">Permite mantener varias secciones expandidas al mismo tiempo.</p>
                  <AccordionGroup multiple class="bg-surface rounded-lg shadow-sm">
                    <Accordion title="Panel C" value="c" color="success">
                      Contenido C
                    </Accordion>
                    <Accordion title="Panel D" value="d" color="success">
                      Contenido D
                    </Accordion>
                  </AccordionGroup>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Control Externo */}
          <Card>
            <Card.Header
              title="Control Programático"
              subtitle="Sincroniza el estado del grupo con la lógica de tu aplicación mediante señales."
              icon={<Icon icon="material-symbols-light:terminal" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col gap-6 p-8 bg-surface-muted rounded-xl border border-border shadow-inner">
                <div class="flex flex-wrap gap-3 mb-4">
                  <Button size="sm" variant="outlined" onClick={() => setOpenValue('step1')} startIcon="material-symbols-light:star-outline">
                    Paso 1
                  </Button>
                  <Button size="sm" variant="outlined" onClick={() => setOpenValue('step2')} startIcon="material-symbols-light:settings-outline">
                    Paso 2
                  </Button>
                  <Button size="sm" variant="outlined" onClick={() => setOpenValue(['step1', 'step2'])} color="secondary">
                    Abrir Todo
                  </Button>
                  <Button size="sm" variant="text" color="danger" onClick={() => setOpenValue(undefined)}>
                    Reset
                  </Button>
                </div>

                <div class="bg-surface rounded-xl shadow-lg border border-border overflow-hidden">
                  <AccordionGroup value={openValue()} onValueChange={setOpenValue} multiple>
                    <Accordion title="Configuración de Perfil" value="step1" icon="material-symbols-light:person-outline">
                      Establece tu nombre de usuario y preferencias de visibilidad.
                    </Accordion>
                    <Accordion title="Preferencias de Seguridad" value="step2" icon="material-symbols-light:security">
                      Configura la autenticación en dos pasos y recuperación de cuenta.
                    </Accordion>
                  </AccordionGroup>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Estética de Grupo */}
          <Card>
            <Card.Header
              title="Apariencia y Divisores"
              subtitle="Personaliza el acabado visual del conjunto y controla la visibilidad de los separadores."
              icon={<Icon icon="material-symbols-light:edit-square-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10">
                <div class="space-y-4">
                  <h4 class="font-bold text-sm text-foreground-subtle">Sin Divisores e Intercalado Personalizado</h4>
                  <div class="p-6 bg-surface-muted rounded-xl border border-border">
                    <AccordionGroup divider={false} class="space-y-2">
                      <Accordion title="Modulo 1" variant="elevation" class="bg-surface">
                        Contenido del módulo 1.
                      </Accordion>
                      <Accordion title="Modulo 2" variant="elevation" class="bg-surface">
                        Contenido del módulo 2.
                      </Accordion>
                    </AccordionGroup>
                  </div>
                </div>

                <div class="space-y-4">
                  <h4 class="font-bold text-sm text-foreground-subtle">Contenedor Compacto con Bordes</h4>
                  <div class="p-6 bg-surface-muted rounded-xl border border-border">
                    <AccordionGroup class="border-2 border-primary-500 rounded-xl overflow-hidden bg-surface">
                      <Accordion title="Información Legal" color="primary">
                        Información detallada sobre términos y condiciones.
                      </Accordion>
                      <Accordion title="Política de Privacidad" color="primary">
                        Cómo tratamos tus datos de forma segura.
                      </Accordion>
                    </AccordionGroup>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Propiedades exclusivas del componente AccordionGroup."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold italic">children</td>
                      <td class="px-6 py-4">JSX.Element</td>
                      <td class="px-6 py-4">-</td>
                      <td class="px-6 py-4 text-balance">Componentes Accordion que forman el grupo.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">multiple</td>
                      <td class="px-6 py-4">boolean</td>
                      <td class="px-6 py-4">false</td>
                      <td class="px-6 py-4">Si es true, permite tener varios elementos abiertos.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">divider</td>
                      <td class="px-6 py-4">boolean</td>
                      <td class="px-6 py-4">true</td>
                      <td class="px-6 py-4">Muestra una línea divisoria entre los acordeones.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">value</td>
                      <td class="px-6 py-4">string | string[]</td>
                      <td class="px-6 py-4">-</td>
                      <td class="px-6 py-4 text-balance">Valores de los acordeones abiertos (modo controlado).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onValueChange</td>
                      <td class="px-6 py-4">function</td>
                      <td class="px-6 py-4">-</td>
                      <td class="px-6 py-4 text-balance">Callback que recibe el nuevo estado (id o lista de ids).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default AccordionGroupPage
